import { simpleGqlClient } from '@/graphql/simple-client.jsx';
import { withIdempotencyKey } from '@/utils/idempotency.js';

function toServiceError(error, fallbackMessage) {
  const message = error?.extensions?.detail || error?.message || fallbackMessage;
  const serviceError = new Error(message);
  serviceError.status = error?.extensions?.status ?? null;
  serviceError.code = error?.extensions?.code ?? null;
  serviceError.detail = error?.extensions?.detail ?? null;
  return serviceError;
}

function throwFirstGraphqlError(result, fallbackMessage) {
  if (!result.errors?.length) return;
  const [firstError] = result.errors;
  throw toServiceError(firstError, fallbackMessage);
}

export async function fetchRetiros({ filter = {}, limit = 20, offset = 0 } = {}, options = {}) {
  const query = `
    query Retiros($filter: RetiroFilter, $limit: Int, $offset: Int) {
      retiros(filter: $filter, limit: $limit, offset: $offset) {
        id
        nroRetiro
        fechaEmision
        estado
        sucursalId
        sucursalEmisoraNombre
        sucursalRetiroLabel
        puntoVentaNumero
        guiaId
        remitenteClienteId
        remitenteRazonSocial
        remitenteDomicilio
        remitenteTelefono
        destinatarioClienteId
        destinatarioRazonSocial
        destinatarioDomicilio
        destinatarioLocalidadId
        destinatarioTelefono
        formaPago
        importe
        remitoCliente
        bultos
        descripcion
        kilosReales
        mts3
        kilosAforados
        valorDeclarado
      }
    }
  `;

  const result = await simpleGqlClient.query(query, { filter, limit, offset }, options);
  throwFirstGraphqlError(result, 'Error al cargar retiros');
  return result.data?.retiros || [];
}

export async function fetchRetirosCount(filter = {}, options = {}) {
  const query = `
    query RetirosCount($filter: RetiroFilter) {
      retirosCount(filter: $filter)
    }
  `;

  const result = await simpleGqlClient.query(query, { filter }, options);
  throwFirstGraphqlError(result, 'Error al cargar el total de retiros');
  return result.data?.retirosCount || 0;
}

export async function fetchRetiroSucursales(options = {}) {
  const query = `
    query RetiroSucursales {
      retiroSucursales { id nombre }
    }
  `;

  const result = await simpleGqlClient.query(query, {}, options);
  throwFirstGraphqlError(result, 'Error al cargar las sucursales');
  return result.data?.retiroSucursales || [];
}

export async function fetchRetiro(id, options = {}) {
  const query = `
    query Retiro($id: ID!) {
      retiro(id: $id) {
        id
        nroRetiro
        fechaEmision
        estado
        sucursalId
        remitenteClienteId
        remitenteRazonSocial
        remitenteDomicilio
        remitenteCp
        remitenteProvinciaId
        remitenteLocalidadId
        remitenteTelefono
        remitenteContacto
        destinatarioClienteId
        destinatarioRazonSocial
        destinatarioDomicilio
        destinatarioCp
        destinatarioProvinciaId
        destinatarioLocalidadId
        destinatarioTelefono
        destinatarioContacto
        formaPago
        importe
        importePendiente
        remitoCliente
        bultos
        descripcion
        kilosReales
        mts3
        kilosAforados
        valorDeclarado
        observacion
        disponibilidadHoraria
        recepcionDt
        cumplirDt
        fechaLimiteDt
        nombreReceptor
        sucursalRetiroLabel
      }
    }
  `;

  const result = await simpleGqlClient.query(query, { id }, options);
  throwFirstGraphqlError(result, 'Error al cargar el retiro');
  return result.data?.retiro || null;
}

export async function createRetiro(input, options = {}) {
  const mutation = `
    mutation CreateRetiro($input: RetiroInput!) {
      createRetiro(input: $input) {
        id
        nroRetiro
        estado
      }
    }
  `;

  const idempotencyOptions = withIdempotencyKey('retiro-create');
  const result = await simpleGqlClient.query(mutation, { input }, {
    ...options,
    fetchOptions: {
      ...(options.fetchOptions || {}),
      ...(idempotencyOptions.fetchOptions || {}),
      headers: {
        ...(options.fetchOptions?.headers || {}),
        ...(idempotencyOptions.fetchOptions?.headers || {}),
      },
    },
  });
  throwFirstGraphqlError(result, 'Error al crear el retiro');
  return result.data?.createRetiro || null;
}

export async function updateRetiro(id, input, options = {}) {
  const mutation = `
    mutation UpdateRetiro($id: ID!, $input: RetiroInput!) {
      updateRetiro(id: $id, input: $input) {
        id
        nroRetiro
        estado
      }
    }
  `;

  const result = await simpleGqlClient.query(mutation, { id, input }, options);
  throwFirstGraphqlError(result, 'Error al actualizar el retiro');
  return result.data?.updateRetiro || null;
}

export async function cambiarEstadoRetiro(id, estado, options = {}) {
  const mutation = `mutation CambiarEstadoRetiro($id: ID!, $estado: RetiroEstado!) {
    cambiarEstadoRetiro(id: $id, estado: $estado) { id estado guiaId }
  }`;
  const result = await simpleGqlClient.query(mutation, { id, estado }, options);
  throwFirstGraphqlError(result, 'Error al cambiar el estado del retiro');
  return result.data?.cambiarEstadoRetiro || null;
}

